/**
 * Editor de texto dos autos (backend: api/routes/editor.py — docs/PLANO_EDITOR_AUTOS.md, Bloco 2).
 *
 * O rascunho é do TITULAR da conta: no contexto delegado, o header de atuação
 * (interceptor do axios) leva as chamadas à conta dele. Versões e estilos usam
 * os tipos do próprio editor forense (camelCase); o envelope do rascunho segue
 * o snake_case da API.
 */
import axios from 'axios';

import type {
  AjustesDosEstilosProntos,
  AlcanceDoModelo,
  DocumentoForense,
  EstiloPersonalizado,
  FormatoParagrafo,
  NomeEstilo,
  SugestaoIA,
  VersaoDoTexto,
} from '@/components/editor-forense';

import { api } from './client';

const BASE = '/editor';
const urlDoRascunho = (numeroProcesso: string) => `${BASE}/rascunhos/${encodeURIComponent(numeroProcesso)}`;

export interface RascunhoServidor {
  html: string;
  documento: DocumentoForense | null;
  /** 0 = ainda não há rascunho no servidor. */
  revisao: number;
  atualizado_em: string | null;
  /** Quem salvou por último (na delegação, pode ser outra pessoa). */
  autor_nome: string | null;
}

/** O PUT chegou com uma revisão velha: outra pessoa (ou aba) salvou antes. */
export class ConflitoDeRascunho extends Error {
  constructor(readonly atual: RascunhoServidor) {
    super('O texto foi salvo em outro lugar depois que você o abriu.');
    this.name = 'ConflitoDeRascunho';
  }
}

export async function fetchRascunho(numeroProcesso: string): Promise<RascunhoServidor> {
  const { data } = await api.get<RascunhoServidor>(urlDoRascunho(numeroProcesso));
  return data;
}

export async function salvarRascunho(
  numeroProcesso: string,
  dados: { html: string; documento: DocumentoForense | null; revisao_base: number },
): Promise<RascunhoServidor> {
  try {
    const { data } = await api.put<RascunhoServidor>(urlDoRascunho(numeroProcesso), dados);
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      const atual = (err.response.data as { atual?: RascunhoServidor } | undefined)?.atual;
      if (atual) throw new ConflitoDeRascunho(atual);
    }
    throw err;
  }
}

/** Formatos em que a minuta pode ser baixada. */
export type FormatoMinuta = 'pdf' | 'docx';

export async function fetchMinuta(numeroProcesso: string, formato: FormatoMinuta): Promise<Blob> {
  const { data } = await api.get<Blob>(`${urlDoRascunho(numeroProcesso)}/minuta.${formato}`, {
    responseType: 'blob',
  });
  return data;
}

/** Versão na lista: sem o texto, com a contagem de palavras (o texto vem ao abrir). */
export type VersaoResumo = Omit<VersaoDoTexto, 'html'> & { palavras: number };

export async function fetchVersoes(numeroProcesso: string): Promise<VersaoResumo[]> {
  const { data } = await api.get<VersaoResumo[]>(`${urlDoRascunho(numeroProcesso)}/versoes`);
  return data;
}

export async function fetchVersao(numeroProcesso: string, id: string): Promise<VersaoDoTexto> {
  const { data } = await api.get<VersaoDoTexto>(
    `${urlDoRascunho(numeroProcesso)}/versoes/${encodeURIComponent(id)}`,
  );
  return data;
}

export async function guardarVersao(numeroProcesso: string, versao: VersaoDoTexto): Promise<VersaoResumo> {
  const { data } = await api.post<VersaoResumo>(`${urlDoRascunho(numeroProcesso)}/versoes`, {
    id: versao.id,
    html: versao.html,
    origem: versao.origem,
    rotulo: versao.rotulo,
  });
  return data;
}

export async function excluirVersao(numeroProcesso: string, id: string): Promise<void> {
  await api.delete(`${urlDoRascunho(numeroProcesso)}/versoes/${encodeURIComponent(id)}`);
}

export interface EstilosDoServidor {
  personalizados: EstiloPersonalizado[];
  ajustesProntos: AjustesDosEstilosProntos;
  /** Pode criar e mudar os estilos de todos (quem administra o escritório). */
  podeEditarEscritorio: boolean;
  /** Os ajustes dos prontos "só para mim"; onde o do escritório ajusta o mesmo campo, vale o dele. */
  ajustesProntosPessoais?: AjustesDosEstilosProntos;
  /** O titular está num escritório (sem ele, a conta é o escritório). */
  temEscritorio?: boolean;
  /** O do escritório vence o de cada membro (obrigatório) ou só vale para quem não tem o seu. */
  padraoObrigatorio?: boolean;
}

export async function fetchEstilos(): Promise<EstilosDoServidor> {
  const { data } = await api.get<EstilosDoServidor>(`${BASE}/estilos`);
  return data;
}

export async function salvarEstilo(e: EstiloPersonalizado): Promise<EstilosDoServidor> {
  const { data } = await api.put<EstilosDoServidor>(`${BASE}/estilos/${encodeURIComponent(e.id)}`, {
    tipo: 'personalizado',
    nome: e.nome,
    baseadoEm: e.baseadoEm,
    ajustes: e.ajustes,
    alcance: e.alcance,
  });
  return data;
}

export async function excluirEstilo(id: string): Promise<EstilosDoServidor> {
  const { data } = await api.delete<EstilosDoServidor>(`${BASE}/estilos/${encodeURIComponent(id)}`);
  return data;
}

export async function ajustarEstiloPronto(
  nome: NomeEstilo,
  ajustes: Partial<FormatoParagrafo> | null,
  alcance: AlcanceDoModelo = 'escritorio',
): Promise<EstilosDoServidor> {
  const url = `${BASE}/estilos/pronto:${nome}`;
  const { data } = ajustes
    ? await api.put<EstilosDoServidor>(url, { tipo: 'pronto', ajustes, alcance })
    : await api.delete<EstilosDoServidor>(url, { params: { alcance } });
  return data;
}

/** Os estilos de UM escritório, pelo id (a aba Peças dele; não o principal de quem pede). */
export async function fetchEstilosDoEscritorio(escritorioId: string): Promise<EstilosDoServidor> {
  const { data } = await api.get<EstilosDoServidor>(`${BASE}/estilos/escritorio/${escritorioId}`);
  return data;
}

/** O ajuste de um pronto do escritório; `null` volta ao padrão. */
export async function ajustarProntoDoEscritorio(
  escritorioId: string,
  nome: NomeEstilo,
  ajustes: Partial<FormatoParagrafo> | null,
): Promise<EstilosDoServidor> {
  const { data } = await api.put<EstilosDoServidor>(`${BASE}/estilos/escritorio/${escritorioId}/pronto/${nome}`, { ajustes });
  return data;
}

/* ---------- Formatação de um arquivo (PLANO_FORMATACAO_DO_ARQUIVO) ---------- */

/** O que o servidor mediu de uma petição, no vocabulário do editor; `null` = não deu para medir. */
export interface FormatacaoMedida {
  formato: 'docx' | 'pdf';
  pagina: {
    larguraCm: number;
    alturaCm: number;
    papel: 'a4' | 'oficio' | 'carta' | null;
    orientacao: 'retrato' | 'paisagem';
    margens: { superior: number | null; inferior: number | null; esquerda: number | null; direita: number | null };
  } | null;
  fonte: { familia: string | null; tamanho: number | null };
  paragrafo: {
    alinhamento: 'left' | 'center' | 'right' | 'justify' | null;
    entrelinha: number | null;
    especial: 'nenhum' | 'primeira' | 'deslocado' | null;
    medida: number | null;
    recuoEsquerdo: number | null;
    recuoDireito: number | null;
    espacoAntes: number | null;
    espacoDepois: number | null;
  };
  /** Opcional: servidor anterior ao timbrado do arquivo não manda. */
  timbrado?: {
    cabecalho: string | null;
    rodape: string | null;
    numeroDePagina: boolean;
    /** O desenho do papel (`data:` PNG/JPEG): a faixa de cada borda, ou o logotipo. */
    imagemCabecalho?: string | null;
    imagemRodape?: string | null;
    logotipo?: string | null;
  } | null;
}

/** Mede a formatação de um DOCX/PDF no servidor; o arquivo não fica guardado. */
export async function extrairFormatacao(arquivo: File): Promise<FormatacaoMedida> {
  const corpo = new FormData();
  corpo.append('arquivo', arquivo);
  // Sem timeout global no client: o teto é o do anexo do chat (upload lento +
  // a leitura no processo filho), senão o painel fica em "Lendo…" para sempre.
  const { data } = await api.post<FormatacaoMedida>(`${BASE}/formatacao/extrair`, corpo, { timeout: 180_000 });
  return data;
}

/* ---------- Revisão por IA (Bloco 4) ---------- */

export interface RevisaoDoServidor {
  sugestoes: SugestaoIA[];
  /** O provedor de IA recusou o texto (a chamada foi cobrada). */
  recusada: boolean;
}

export async function revisarTexto(numeroProcesso: string, texto: string): Promise<RevisaoDoServidor> {
  const { data } = await api.post<RevisaoDoServidor>(`${urlDoRascunho(numeroProcesso)}/revisar`, { texto });
  return data;
}

/** A revisão de um texto que não é de processo (um modelo): na conta de quem escreve. */
export async function revisarTextoSemProcesso(texto: string): Promise<RevisaoDoServidor> {
  const { data } = await api.post<RevisaoDoServidor>('/editor/revisar', { texto });
  return data;
}
